import { useState, useEffect } from 'react'
import { Link, Outlet } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Settings, Menu, LogOut } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { getDoctorProfile } from '@/lib/supabase'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'

export default function Layout() {
  const { user, signOut } = useAuth()
  const [profile, setProfile] = useState<{ first_name: string; last_name: string } | null>(null)

  useEffect(() => {
    if (user) {
      getDoctorProfile(user.id).then(data => {
        setProfile(data)
      })
    }
  }, [user])

  const getInitials = () => {
    if (!profile) return '';
    return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header Simplificado */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 sm:h-16 items-center justify-between">
          {/* Logo e Link */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity">
              <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary shadow-lg backdrop-blur-sm bg-opacity-90">
                <svg
                  className="w-4 h-4 sm:w-6 sm:h-6 text-primary-foreground"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 12h2l2 -8 2 16 2 -12 2 8 1.5 -3 2 0 1 -2 3.5 0" />
                  <path d="M3 8h18" strokeOpacity="0.3" strokeWidth="1" />
                  <path d="M3 16h18" strokeOpacity="0.3" strokeWidth="1" />
                </svg>
              </div>
              <span className="font-semibold text-base sm:text-lg">Simples.ECG</span>
            </Link>

            <a 
              href="https://simples.health" 
              target="_blank" 
              rel="noreferrer" 
              className="flex items-center gap-1 text-[10px] sm:text-sm text-muted-foreground hover:text-primary transition-colors ml-1 sm:ml-2"
            >
              por Simples.Health
              <svg 
                className="w-2.5 h-2.5 sm:w-3 sm:h-3" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" 
                />
              </svg>
            </a>
          </div>

          {/* Elementos da direita */}
          <div className="flex items-center gap-3 sm:gap-6">
            {/* Avatar com iniciais */}
            <div className="hidden md:flex items-center gap-3">
              <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
                <AvatarFallback className="bg-primary text-primary-foreground text-sm sm:text-base">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Botões de Ação */}
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/settings">
                <Button variant="ghost" size="icon" className="hidden md:flex h-8 w-8 sm:h-12 sm:w-12">
                  <Settings className="h-4 w-4 sm:h-6 sm:w-6" />
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={signOut} className="h-8 w-8 sm:h-12 sm:w-12">
                <LogOut className="h-4 w-4 sm:h-6 sm:w-6" />
              </Button>
            </div>

            {/* Menu Mobile */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden h-8 w-8 sm:h-12 sm:w-12">
                  <Menu className="h-4 w-4 sm:h-6 sm:w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px]">
                <nav className="flex flex-col gap-6 mt-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 sm:h-10 sm:w-10">
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm sm:text-base">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="text-base sm:text-lg text-muted-foreground">
                      {profile?.first_name} {profile?.last_name}
                    </div>
                  </div>
                  <Link to="/settings" className="flex items-center gap-3 text-lg sm:text-xl font-medium">
                    <Settings className="h-5 w-5 sm:h-6 sm:w-6" />
                    Configurações
                  </Link>
                  <Button variant="ghost" className="justify-start h-10 sm:h-12 text-base sm:text-lg" onClick={signOut}>
                    <LogOut className="h-5 w-5 sm:h-6 sm:w-6 mr-3" />
                    Sair
                  </Button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Conteúdo Principal */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t py-4 sm:py-6 md:py-0">
        <div className="container flex flex-col items-center justify-between gap-2 sm:gap-4 md:h-14 md:flex-row">
          <p className="text-center text-xs sm:text-sm leading-loose text-muted-foreground md:text-left">
            Desenvolvido por{" "}
            <a
              href="https://simples.health"
              target="_blank"
              rel="noreferrer"
              className="font-medium underline underline-offset-4"
            >
              Simples.Health
            </a>
            . Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  )
} 